import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Marker, Polyline, Popup } from 'react-leaflet';
import { Loader2, Search } from 'lucide-react';

import MapSurface from './MapSurface';
import { dotIcon } from './icons';
import { MAP_LAYERS, DEFAULT_CENTER } from './layers';
import { distanceMeters, toLatLng } from './geo';
import defaultSearch from './search';

/** A geofence is useless below a handful of metres and absurd above 200 km. */
const MIN_RADIUS_M = 10;
const MAX_RADIUS_M = 200000;

/** Placing a border post is a ten-metre job, so the picker opens at street
 *  level. It used to open at zoom 5 on 288 px: one pixel was ±5 km and the
 *  view never moved to the point being edited. */
const POINT_ZOOM = 15;

const PICKER_LAYERS = ['satellite', 'streets'].filter((cle) => MAP_LAYERS[cle]);

/** Handle sits due east of the centre; one degree of longitude shrinks with
 *  latitude, which matters at the equator-adjacent latitudes we operate in. */
function radiusHandlePosition(center, radiusMeters) {
  const [lat, lng] = center;
  const metresPerDegree = 111320 * Math.cos((lat * Math.PI) / 180);
  if (!Number.isFinite(metresPerDegree) || metresPerDegree === 0) return [lat, lng];
  return [lat, lng + radiusMeters / metresPerDegree];
}

export default function ZonePicker({
  latitude,
  longitude,
  radiusMeters = 500,
  name,
  onChange,
  onSearch = defaultSearch,
  className = '',
  height = 'h-[420px] md:h-[480px]',
}) {
  const point = toLatLng([latitude, longitude]);
  const radius = Math.min(
    MAX_RADIUS_M,
    Math.max(MIN_RADIUS_M, Number(radiusMeters) || 500),
  );

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dragCenter, setDragCenter] = useState(null);
  const [dragRadius, setDragRadius] = useState(null);
  const [layer, setLayer] = useState(PICKER_LAYERS[0]);

  // Dragging the marker feeds the position back through the parent form. Without
  // this record the map would then re-centre on the point the operator just
  // moved, fighting the drag. Only positions arriving from elsewhere — search,
  // a typed coordinate, opening an existing zone — earn a fly-to.
  const emittedRef = useRef(null);
  const [focus, setFocus] = useState(point);
  const [focusZoom, setFocusZoom] = useState(point ? POINT_ZOOM : undefined);

  const lat = point?.[0] ?? null;
  const lng = point?.[1] ?? null;

  useEffect(() => {
    if (lat == null || lng == null) return;
    const emitted = emittedRef.current;
    if (emitted && emitted[0] === lat && emitted[1] === lng) return;
    setFocus([lat, lng]);
    setFocusZoom(POINT_ZOOM);
  }, [lat, lng]);

  const emitPosition = useCallback(
    (next, extra) => {
      const value = toLatLng(next);
      if (!value) return;
      emittedRef.current = [
        Number(value[0].toFixed(6)),
        Number(value[1].toFixed(6)),
      ];
      onChange?.({
        latitude: value[0].toFixed(6),
        longitude: value[1].toFixed(6),
        ...extra,
      });
    },
    [onChange],
  );

  const emitRadius = useCallback(
    (meters) => {
      const value = Math.round(
        Math.min(MAX_RADIUS_M, Math.max(MIN_RADIUS_M, meters)),
      );
      onChange?.({ radius_meters: value });
    },
    [onChange],
  );

  const runSearch = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const text = query.trim();
      if (!text) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const rows = await onSearch(text);
        setResults(Array.isArray(rows) ? rows : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    },
    [onSearch, query],
  );

  const pickResult = useCallback(
    (result) => {
      setResults([]);
      setQuery(result.name || '');
      // A result carrying its own radius is an existing zone: adopting it makes
      // duplicating a checkpoint a single click instead of a re-measurement.
      emitPosition([result.latitude, result.longitude]);
      setFocus([result.latitude, result.longitude]);
      setFocusZoom(POINT_ZOOM);
      if (result.radius_meters) emitRadius(result.radius_meters);
    },
    [emitPosition, emitRadius],
  );

  const center = dragCenter || point;
  const shownRadius = dragRadius ?? radius;
  const handlePosition = useMemo(
    () => (center ? radiusHandlePosition(center, shownRadius) : null),
    [center, shownRadius],
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <form onSubmit={runSearch} className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/70"
            aria-hidden="true"
          />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Nom du lieu, ou « 12.368, -1.527 »"
            aria-label="Rechercher un lieu"
            className="w-full rounded-md border border-input bg-background py-1.5 pl-7 pr-3 text-sm"
          />
        </div>
        <button type="submit" className="btn-secondary px-3 py-1.5 text-sm">
          {searching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            'Chercher'
          )}
        </button>
        <div className="flex gap-1">
          {PICKER_LAYERS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setLayer(key)}
              aria-pressed={layer === key}
              className={`rounded border px-2 py-1 text-xs transition ${
                layer === key
                  ? 'border-info bg-info text-info-foreground'
                  : 'border-border bg-card hover:bg-muted/50'
              }`}
            >
              {MAP_LAYERS[key].label}
            </button>
          ))}
        </div>
      </form>

      {results.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-card text-sm">
          {results.map((result) => (
            <li key={result.id}>
              <button
                type="button"
                onClick={() => pickResult(result)}
                className="block w-full px-3 py-2 text-left hover:bg-muted/60"
              >
                <span className="block font-medium text-foreground">{result.name}</span>
                {result.subtitle && (
                  <span className="block text-xs text-muted-foreground">
                    {result.subtitle}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        className={`relative isolate z-0 w-full min-w-0 overflow-hidden rounded-lg border border-border ${height}`}
      >
        <MapSurface
          layer={layer}
          center={point || DEFAULT_CENTER}
          zoom={point ? POINT_ZOOM : 6}
          focus={focus}
          focusZoom={focusZoom}
          focusAnimate={false}
          fitEnabled={false}
          onMapClick={emitPosition}
        >
          {center && (
            <>
              <Circle
                center={center}
                radius={shownRadius}
                interactive={false}
                pathOptions={{
                  color: 'hsl(var(--info))',
                  fillColor: 'hsl(var(--info))',
                  fillOpacity: 0.18,
                  weight: 2,
                }}
              />
              {handlePosition && (
                <>
                  {/* The spoke makes the handle read as the radius rather than
                      as a second, unrelated point on the map. */}
                  <Polyline
                    positions={[center, handlePosition]}
                    interactive={false}
                    pathOptions={{
                      color: 'hsl(var(--info))',
                      weight: 1,
                      dashArray: '4 4',
                      opacity: 0.8,
                    }}
                  />
                  <Marker
                    position={handlePosition}
                    draggable
                    icon={dotIcon({ color: 'hsl(var(--flame))', size: 16 })}
                    eventHandlers={{
                      drag: (event) =>
                        setDragRadius(
                          distanceMeters(center, event.target.getLatLng()),
                        ),
                      dragend: (event) => {
                        emitRadius(distanceMeters(center, event.target.getLatLng()));
                        setDragRadius(null);
                      },
                    }}
                  >
                    <Popup>Faites glisser pour régler le rayon</Popup>
                  </Marker>
                </>
              )}
              <Marker
                position={center}
                draggable
                eventHandlers={{
                  drag: (event) => {
                    const next = event.target.getLatLng();
                    setDragCenter([next.lat, next.lng]);
                  },
                  dragend: (event) => {
                    const next = event.target.getLatLng();
                    emitPosition([next.lat, next.lng]);
                    setDragCenter(null);
                  },
                }}
              >
                <Popup>{name || 'Nouvelle localisation'}</Popup>
              </Marker>
            </>
          )}
        </MapSurface>
      </div>

      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <Aide titre="Placer la zone">
          Cliquez pour poser le point, glissez le repère pour l’ajuster, glissez la
          poignée orange pour le rayon.
        </Aide>
        {point ? (
          <span className="font-mono tabular text-foreground">
            {point[0].toFixed(6)}, {point[1].toFixed(6)} · {Math.round(shownRadius)} m
          </span>
        ) : (
          'Aucun point posé.'
        )}
      </p>
    </div>
  );
}
